import angular from 'angular';
import to from 'await-to-js';
import _ from 'lodash';
import { API } from '../../vendor/api';
import { AngularServices } from '../../modules/core';
const cycleLabels = {
    day: 'common.oneDay',
    month: 'common.monthly',
    year: 'common.annually',
};
const cycleLabelsShort = {
    day: 'day',
    month: 'mo',
    year: 'yr',
};
function getCycleLabel(interval) {
    return cycleLabels[interval];
}
function getCycleLabelShort(interval) {
    return cycleLabelsShort[interval];
}
function extractSubscription(customer) {
    return customer.subscriptions?.data[0];
}
function _createCardElement({ stripeService, container, $translate, addSource, }) {
    const elements = stripeService.elements();
    const options = {
        hidePostalCode: false,
        style: {
            base: {
                fontFamily: 'ProximaNova, sans-serif',
            },
        },
    };
    const stripeElement = elements.create('card', options);
    stripeElement.mount(container);
    const card = {
        error: {
            message: $translate.instant('apps.payment.action.card-info'),
        },
        element: stripeElement,
        clear: function () {
            this.element.clear();
        },
        save: async function (captchaJWTToken) {
            const token = await this.createToken();
            const res = await addSource(token, captchaJWTToken);
            return res;
        },
        handleCardPayment: async (clientSecret) => {
            // migrate off of depreciated method: https://stripe.com/docs/js/deprecated/handle_card_payment_element
            // @ts-expect-error Depreciated but still exists
            const response = await stripeService.handleCardPayment(clientSecret, stripeElement);
            if (response.error != null) {
                throw response.error;
                // Display error.message in your UI.
            }
            else {
                return response;
                // The payment has succeeded
                // Display a success message
            }
        },
        createToken: async function () {
            if (this.error != null) {
                throw new Error(this.error.message);
            }
            const tokenOptions = {};
            // set cardholder name
            const cardHolderEl = container.parentNode.querySelector('#CardHolder');
            if (cardHolderEl != null) {
                tokenOptions.name = cardHolderEl.value;
            }
            const res = await stripeService.createToken(stripeElement, tokenOptions);
            if (res.error != null) {
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw res.error;
            }
            return res.token;
        },
    };
    stripeElement.addEventListener('change', (event) => {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        card.error = event.error || null;
        if (event.empty) {
            card.error = {
                message: $translate.instant('apps.payment.action.card-info'),
            };
        }
    });
    return card;
}
/* @ngInject */
function PaymentService($translate, User, Purchase, stripe) {
    const cache = {};
    function ensureUserDefined(user) {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        return user || User.getCachedCurrent();
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async function getCustomer(user) {
        const currentUser = ensureUserDefined(user);
        return User.prototype$getCustomer({ id: currentUser.id }).$promise;
    }
    function extractDefaultCard(customer) {
        if (!customer || !customer.sources) {
            return null;
        }
        const defaultCard = customer.sources.data.find((source) => source.id === customer.default_source);
        if (!defaultCard.last4) {
            return null;
        }
        cache.defaultCard = {
            brand: defaultCard.brand,
            name: defaultCard.name,
            last4: defaultCard.last4,
            exp_month: defaultCard.exp_month,
            exp_year: defaultCard.exp_year,
            address_zip: defaultCard.address_zip,
            country: defaultCard.country,
        };
        return cache.defaultCard;
    }
    async function extractDefaultPaymentMethod(customerId, paymentMethodId) {
        const defaultPaymentMethod = await API.stripe.getCustomerPaymentMethod({
            customerId,
            paymentMethodId,
        });
        if (!Boolean(defaultPaymentMethod.card?.last4)) {
            return null;
        }
        cache.defaultCard = {
            brand: defaultPaymentMethod.card.brand,
            name: defaultPaymentMethod.billing_details.name,
            last4: defaultPaymentMethod.card.last4,
            exp_month: defaultPaymentMethod.card.exp_month,
            exp_year: defaultPaymentMethod.card.exp_year,
            address_zip: defaultPaymentMethod.billing_details.address.postal_code,
            country: defaultPaymentMethod.billing_details.address.country,
        };
        return cache.defaultCard;
    }
    async function getDefaultCard(user) {
        const currentUser = ensureUserDefined(user);
        const customer = await getCustomer(currentUser);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (customer?.invoice_settings?.default_payment_method != null) {
            return extractDefaultPaymentMethod(customer.id, customer.invoice_settings.default_payment_method);
        }
        return extractDefaultCard(customer);
    }
    async function addSource(token, captchaJWTToken, user) {
        const currentUser = ensureUserDefined(user);
        const userToken = typeof token === 'object' && token.id ? token.id : token;
        const card = await User.prototype$addStripeSource({
            id: currentUser.id,
        }, { token: userToken, captchaJWTToken }).$promise;
        cache.defaultCard = {
            brand: card.brand,
            name: card.name,
            last4: card.last4,
            exp_month: card.exp_month,
            exp_year: card.exp_year,
            address_zip: card.address_zip,
            country: card.country,
        };
        return cache.defaultCard;
    }
    async function getCharges(user) {
        const currentUser = ensureUserDefined(user);
        if (currentUser.customerId) {
            const charges = await User.prototype$getCharges({
                id: currentUser.id,
            }).$promise;
            return charges.data;
        }
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async function getNext(user) {
        const currentUser = ensureUserDefined(user);
        return User.prototype$previewNextPayment({ id: currentUser.id }).$promise;
    }
    // eslint-disable-next-line @typescript-eslint/require-await
    async function isValidCoupon(code, currency, product) {
        return Purchase.validateCoupon({ code, currency, product }).$promise;
    }
    // generally requires paymentIntent ID and the card details
    async function confirmCardPayment(...paymentDetails) {
        const stripeInstance = await stripe.getDoxyStripeInstance();
        return stripeInstance.confirmCardPayment(...paymentDetails);
    }
    async function createDoxyStripeCardElement(container) {
        const stripeService = await stripe.getDoxyStripeInstance();
        return _createCardElement({
            stripeService,
            container,
            $translate,
            addSource,
        });
    }
    async function createCustomStripeCardElement(container, customStripePublishable) {
        const stripeService = await stripe.getCustomStripeInstance(customStripePublishable);
        return _createCardElement({
            stripeService,
            container,
            $translate,
            addSource,
        });
    }
    async function updateSubscription(id, options) {
        const [updateError, updatedPurchase] = await to(Purchase.prototype$updateAttributes({ id }, options).$promise);
        // https://stripe.com/docs/billing/subscriptions/upgrading-downgrading
        if (updateError) {
            const paymentIntentSecret = _.get(updateError, 'data.error.data.paymentIntentSecret');
            if (!paymentIntentSecret) {
                throw updateError;
            }
            const stripeInstance = await stripe.getDoxyStripeInstance();
            const result = await stripeInstance.confirmCardPayment(paymentIntentSecret);
            if (result.error != null) {
                throw new Error($translate.instant('apps.payment.payment-failure'));
            }
            else {
                const paymentIntentId = result.paymentIntent.id;
                const [, purchase] = await to(Purchase.completePurchase({ paymentIntentId }).$promise);
                return purchase;
            }
        }
        else {
            return updatedPurchase;
        }
    }
    async function createSubscription(planId, coupon, captchaJWTToken, seats = 1) {
        const [err, purchase] = await to(Purchase.create({
            planId,
            coupon,
            seats,
            captchaJWTToken,
        }).$promise);
        if (err) {
            const paymentIntentSecret = _.get(err, 'data.error.data.paymentIntentSecret');
            if (!paymentIntentSecret) {
                throw err;
            }
            const stripeInstance = await stripe.getDoxyStripeInstance();
            const result = await stripeInstance.confirmCardPayment(paymentIntentSecret);
            if (result.error != null) {
                throw new Error($translate.instant('apps.payment.payment-failure'));
            }
            else {
                const paymentIntentId = result.paymentIntent.id;
                const [, completePurchase] = await to(Purchase.completePurchase({ paymentIntentId }).$promise);
                return completePurchase;
            }
        }
        else {
            return purchase;
        }
    }
    return {
        getCustomer,
        getDefaultCard,
        extractDefaultCard,
        extractSubscription,
        addSource,
        getCharges,
        getNext,
        getCycleLabel,
        getCycleLabelShort,
        isValidCoupon,
        confirmCardPayment,
        createDoxyStripeCardElement,
        createCustomStripeCardElement,
        updateSubscription,
        createSubscription,
    };
}
export default angular
    .module('doxy.common.services.payment', [])
    .factory(AngularServices.Payment, PaymentService).name;
